import { Controller } from "@hotwired/stimulus"

// Remembers this element's scroll position across Turbo-driven re-renders.
// The check pane is a full-page replacement on every interaction, so without
// this every quantity bump or note save snaps the scroll back to the top.
//
// Usage:
//   <div data-controller="scroll-memory" data-scroll-memory-key-value="pos-check-pane">
//
// Restore strategy: sessionStorage survives the Turbo navigation. On connect
// we keep trying to set scrollTop until either the content is tall enough to
// honor it (scrollHeight catches up) or we hit a safety timeout — this handles
// the race where the child DOM is still materializing when connect() fires.
export default class extends Controller {
  static values = { key: String }

  connect() {
    this.storageKey = `scroll-memory:${this.keyValue || this.element.id || "default"}`
    this.#restore()
    this.onScroll = this.#persist.bind(this)
    this.element.addEventListener("scroll", this.onScroll, { passive: true })

    // Also persist just before Turbo navigates away — disconnect doesn't
    // always fire before the transition on some browsers.
    this.onBeforeVisit = this.#persist.bind(this)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("turbo:submit-start", this.onBeforeVisit)
    document.addEventListener("turbo:before-cache", this.onBeforeVisit)
  }

  disconnect() {
    this.#persist()
    this.element.removeEventListener("scroll", this.onScroll)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("turbo:submit-start", this.onBeforeVisit)
    document.removeEventListener("turbo:before-cache", this.onBeforeVisit)
  }

  #persist() {
    // Don't overwrite a good value with 0 when the element is being torn down
    // and its content is already gone (scrollHeight collapses → scrollTop→0).
    if (this.element.scrollHeight <= this.element.clientHeight) return
    window.sessionStorage.setItem(this.storageKey, String(this.element.scrollTop))
  }

  #restore() {
    const saved = window.sessionStorage.getItem(this.storageKey)
    if (saved == null) return
    const target = parseInt(saved, 10)
    if (!Number.isFinite(target) || target <= 0) return

    // Retry until the element has grown tall enough to honor the position, or
    // 500ms has passed (typical page render is <50ms).
    const deadline = performance.now() + 500
    const tryScroll = () => {
      this.element.scrollTop = target
      if (this.element.scrollTop < target && performance.now() < deadline) {
        requestAnimationFrame(tryScroll)
      }
    }
    requestAnimationFrame(tryScroll)
  }
}
